import { z } from 'zod';
import { UnknownObject, DateTimeSchema, ObjectIdSchema, ObjectIdString } from '../../common.js';
import * as R from 'remeda';
import { toNonEmpty } from '../../../utils.js';
import {
  dataIqOperationStepConfigs,
  DataIqOperationStepName,
  DataIqOperationType,
  dataIqOperationTypeConfigs,
} from './dataIqOperationSchema.js';
import {
  discoveryOperationConfigs,
  discoveryOperationStepConfigs,
  DiscoveryOperationStepName,
  DiscoveryOperationType,
} from './discoveryOperationSchema.js';
import { TypeConfig, StepConfig } from './operationSharedTypes.js';

export * from './dataIqOperationSchema.js';
export * from './discoveryOperationSchema.js';
export type OperationTypeConfig = TypeConfig<OperationStepName>;

export const OperationType = z.enum([
  ...DataIqOperationType.options,
  ...DiscoveryOperationType.options,
]);
export type OperationType = z.infer<typeof OperationType>;

// List of steps for each operation type
export const OPERATION_TYPE_CONFIGS = {
  ...dataIqOperationTypeConfigs,
  ...discoveryOperationConfigs,
} as const satisfies Record<OperationType, OperationTypeConfig>;

export const parentOperationTypes = R.pipe(
  OPERATION_TYPE_CONFIGS,
  R.pickBy((config) => 'hasChildOperations' in config && config.hasChildOperations),
  R.keys()
);
export const isParentOperationType = R.isIncludedIn(parentOperationTypes);

// Statuses for operations that are done processing
export const CompletedOperationStatus = z.enum(['complete', 'failed', 'skipped', 'terminated']);
export type CompletedOperationStatus = z.infer<typeof CompletedOperationStatus>;

export const isCompletedOperationStatus = R.isIncludedIn(CompletedOperationStatus.options);

export const OperationStatus = z.enum([
  'new',
  'queued',
  'processing',
  'stepComplete',
  'waitingForChildren',
  ...CompletedOperationStatus.options,
]);
export type OperationStatus = z.infer<typeof OperationStatus>;

export const OperationStepName = z.enum([
  ...DataIqOperationStepName.options,
  ...DiscoveryOperationStepName.options,
]);
export type OperationStepName = z.infer<typeof OperationStepName>;

export const operationStepConfigs = {
  ...dataIqOperationStepConfigs,
  ...discoveryOperationStepConfigs,
} as const satisfies Record<OperationStepName, StepConfig>;

type OperationStepConfig<T extends OperationStepName> = (typeof operationStepConfigs)[T];
export type OperationStepInput<T extends OperationStepName> = z.infer<
  OperationStepConfig<T>['inputSchema']
>;
export type OperationStepRawInput<T extends OperationStepName> = z.input<
  OperationStepConfig<T>['inputSchema']
>;
export type OperationStepOutput<T extends OperationStepName> = z.infer<
  OperationStepConfig<T>['outputSchema']
>;
export type OperationStepRawOutput<T extends OperationStepName> = z.input<
  OperationStepConfig<T>['outputSchema']
>;

export const BaseOperationStep = z.object({
  _id: ObjectIdSchema,
  stepName: OperationStepName,
  pubSubId: z.string().nullish(),
  input: z.record(z.string(), z.unknown()).nullish(),
  result: z.record(z.string(), z.unknown()).nullish(),
  error: UnknownObject.nullish(),
  startTime: DateTimeSchema.nullish(),
  endTime: DateTimeSchema.nullish(),
});
export type BaseOperationStep = z.infer<typeof BaseOperationStep>;
export const OperationStep = z.discriminatedUnion(
  'stepName',
  toNonEmpty(
    R.map(OperationStepName.options, (name) => {
      const { inputSchema, outputSchema } = operationStepConfigs[name];
      return z.object({
        ...BaseOperationStep.shape,
        stepName: z.literal(name),
        input: inputSchema.nullish(),
        result: outputSchema.nullish(),
      });
    })
  )
);
export type OperationStep = BaseOperationStep & z.infer<typeof OperationStep>;

export const OperationStepDTO = z.object({
  ...BaseOperationStep.shape,
  _id: ObjectIdString,
});
export type OperationStepDTO = z.infer<typeof OperationStepDTO>;

//endregion
//--------------------------------------------------------------------------------------------------------------------//

export const CandidateSubDTO = z.object({
  _id: ObjectIdString,
  name: z.string().optional(),
  firstName: z.string(),
  lastName: z.string(),
  sourceId: z.string(),
});

export type CandidateSubDTO = z.infer<typeof CandidateSubDTO>;

export const IOperation = z.object({
  operationType: OperationType,
  status: OperationStatus,
  account: ObjectIdSchema,
  integration: ObjectIdSchema,
  candidate: ObjectIdSchema.nullish(),
  parentOperation: ObjectIdSchema.nullish(),
  replayOfOperation: ObjectIdSchema.nullish(),

  // The schema doesn't care about the specific step input/output formats at this level
  steps: BaseOperationStep.array(),

  completedDate: DateTimeSchema.nullish(),
  createdAt: DateTimeSchema,
  updatedAt: DateTimeSchema,
});
export type IOperation = z.infer<typeof IOperation>;

// Version of the model for the frontend
export const OperationDTO = z.object({
  ...IOperation.shape,
  _id: ObjectIdString,
  account: ObjectIdString,
  integration: ObjectIdSchema,
  candidate: z.union([ObjectIdString, CandidateSubDTO]).nullish(),
  replayOfOperation: ObjectIdString.nullish(),
  steps: OperationStepDTO.array(),
});
export type OperationDTO = z.infer<typeof OperationDTO>;

export const ChildOperationDTO = OperationDTO.extend({
  candidate: CandidateSubDTO.nullish(),
});
export type ChildOperationDTO = z.infer<typeof ChildOperationDTO>;

export const OperationDetailDTO = z.object({
  ...OperationDTO.shape,
  // TODO: add a fancy wrapper to omit unpopulated IDs so we don't get an error
  candidate: CandidateSubDTO.nullish(),
  childOperations: ChildOperationDTO.array().nullish(),
});
export type OperationDetailDTO = z.infer<typeof OperationDetailDTO>;
